import { closeView } from '@src/components/OMS/OMSSlice';
import { useOMSDependencies } from '@src/components/OMS/useOMSDependencies';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { LoaderTalos, useMobileModal } from '@talos/kyoko';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { useCardsService } from '../../../components/OMS/Cards/useCardsService';

const MobileOMSModal = lazy(() => import('./MobileOMSModal').then(module => ({ default: module.MobileOMSModal })));

export function MobileOMS() {
  // Pre-heat the OMS dependencies.
  useOMSDependencies();
  useCardsService();
  const omsView = useAppStateSelector(state => state.OMS.openedView);
  const { openModal, closeModal, pushModal, popModal, isModalOpen } = useMobileModal();
  const dispatch = useAppStateDispatch();

  // Keep OMS view changes inside the same sheet so switching forms feels like in-modal navigation.
  const isOmsModalOpenRef = useRef(false);
  const didPushModalRef = useRef(false);
  // Track isModalOpen via ref so reading it in the effect below doesn't add it to the deps array.
  // Adding isModalOpen to deps causes extra re-runs whenever a modal opens (since pushModal's identity
  // also changes when config updates after openModal), which can race with omsView changes.
  const isModalOpenRef = useRef(false);
  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const onCloseModal = useCallback(() => {
    isOmsModalOpenRef.current = false;
    didPushModalRef.current = false;
    dispatch(closeView());
  }, [dispatch]);

  const omsContent = useMemo(
    () => (
      <Suspense fallback={<LoaderTalos />}>
        <MobileOMSModal />
      </Suspense>
    ),
    []
  );

  // Sync Redux's current OMS view with the shared mobile sheet controller.
  useEffect(() => {
    if (omsView) {
      if (!isOmsModalOpenRef.current) {
        isOmsModalOpenRef.current = true;
        if (isModalOpenRef.current) {
          // A modal is already showing (e.g. order detail sheet) — push OMS on top so back can restore it.
          didPushModalRef.current = true;
          pushModal({ content: omsContent, onCloseModal });
        } else {
          didPushModalRef.current = false;
          openModal({ content: omsContent, onCloseModal });
        }
      }
    } else if (isOmsModalOpenRef.current) {
      isOmsModalOpenRef.current = false;
      if (didPushModalRef.current) {
        didPushModalRef.current = false;
        popModal();
      } else {
        closeModal();
      }
    }
  }, [omsView, openModal, pushModal, popModal, closeModal, onCloseModal, omsContent]);

  return null;
}
